import { Injectable } from '@angular/core';
import { Preferences } from '@capacitor/preferences';
import { firstValueFrom } from 'rxjs';
import {
  AppleOutboundAck,
  AppleOutboundFeedRequest,
  ConfigureAppleOutboundRequest,
  DisconnectHealthConnectionRequest,
  HealthHistoryRunRequest,
  NutritionAmbitionApiService
} from './nutrition-ambition-api.service';
import { HealthPlatformService } from './health-platform.service';

const INSTALLATION_ID_KEY = 'health-apple-installation-id';
type OutboundPlatform = 'AppleHealth' | 'HealthConnect';

/**
 * Delivers only server-approved, confirmed meals to the current Apple Health
 * installation. Acknowledgements are sent only after HealthKit saved a meal,
 * which keeps retries idempotent through the native sync identifier/version.
 */
@Injectable({ providedIn: 'root' })
export class AppleHealthOutboundSyncService {
  private isSyncing = false;
  private isDisconnected = false;

  constructor(
    private readonly api: NutritionAmbitionApiService,
    private readonly healthPlatform: HealthPlatformService
  ) {}

  async configure(window: 'today' | 'allHistory', platform: OutboundPlatform = 'AppleHealth'): Promise<void> {
    const installationId = await this.getInstallationId(platform);
    await firstValueFrom(this.api.configureAppleOutbound(new ConfigureAppleOutboundRequest({
      installationId,
      window: window === 'allHistory' ? 'AllHistory' : 'StartToday',
      platform,
      timeZoneId: Intl.DateTimeFormat().resolvedOptions().timeZone
    })));
    this.isDisconnected = false;
  }

  /** Best-effort automatic foreground sync. It never opens an OS prompt. */
  async syncIfConfigured(platform: OutboundPlatform = 'AppleHealth'): Promise<void> {
    if (this.isSyncing) return;
    try {
      const setup = await Preferences.get({ key: this.setupCompleteKey(platform) });
      if (this.isDisconnected || setup.value !== 'true') return;

      if (platform === 'AppleHealth') {
        const authorization = await this.healthPlatform.getAppleHealthAuthorizationState();
        if (authorization?.mealSharing !== 'authorized') return;
      }

      this.isSyncing = true;
      const installationId = await this.getInstallationId(platform);
      let cursor: number | undefined;
      do {
        const feed = await firstValueFrom(this.api.appleOutboundFeed(
          new AppleOutboundFeedRequest({ installationId, platform, cursor })
        ));
        for (const meal of feed.meals ?? []) {
        if (this.isDisconnected) return;
        if (!meal.mealId || !meal.stableIdentifier || meal.version == null) {
          continue;
        }

        const result = meal.operation === 'delete'
          ? await this.healthPlatform.deleteMeal({ stableIdentifier: meal.stableIdentifier, version: meal.version })
          : !meal.name || !meal.startDate || meal.energyKcal == null
            ? null
            : await this.healthPlatform.upsertMeal({
                stableIdentifier: meal.stableIdentifier,
                version: meal.version,
                name: meal.name,
                startDate: meal.startDate.toISOString(),
                energyKcal: meal.energyKcal,
                proteinGrams: meal.proteinGrams,
                carbohydrateGrams: meal.carbohydrateGrams,
                fatGrams: meal.fatGrams
              });
        if (result?.status !== 'saved') continue;

          await firstValueFrom(this.api.acknowledgeAppleOutbound(new AppleOutboundAck({
          installationId,
          mealId: meal.mealId,
          stableIdentifier: meal.stableIdentifier,
          version: meal.version,
          platform
          })));
        }
        cursor = feed.nextCursor;
      } while (cursor != null && !this.isDisconnected);
    } catch {
      // Health and network work are best effort; the next foreground attempt retries safely.
    } finally {
      this.isSyncing = false;
    }
  }

  /** Stops this installation's future outbound health feed work. */
  async disconnect(platform: OutboundPlatform = 'AppleHealth'): Promise<boolean> {
    const existing = await Preferences.get({ key: this.installationIdKey(platform) });
    this.isDisconnected = true;
    await Preferences.remove({ key: this.setupCompleteKey(platform) });
    if (!existing.value) return true;

    try {
      await firstValueFrom(this.api.disconnect(new DisconnectHealthConnectionRequest({
        installationId: existing.value,
        platform
      })));
      return true;
    } catch {
      return false;
    }
  }

  /** Queues deletion of only meals this installation previously shared. */
  async removeSharedMeals(platform: OutboundPlatform = 'AppleHealth'): Promise<boolean> {
    try {
      const installationId = await this.getInstallationId(platform);
      await firstValueFrom(this.api.removeSharedMeals(new HealthHistoryRunRequest({ installationId, platform })));
      await this.syncIfConfigured(platform);
      return true;
    } catch { return false; }
  }

  private async getInstallationId(platform: OutboundPlatform): Promise<string> {
    const key = this.installationIdKey(platform);
    const existing = await Preferences.get({ key });
    if (existing.value) return existing.value;

    const installationId = crypto.randomUUID();
    await Preferences.set({ key, value: installationId });
    return installationId;
  }

  private setupCompleteKey(platform: OutboundPlatform): string {
    return platform === 'HealthConnect' ? 'health-connect-outbound-setup-complete' : 'health-apple-outbound-setup-complete';
  }

  private installationIdKey(platform: OutboundPlatform): string {
    // Health Connect activity and meal sharing are one connection on the same
    // phone. Sharing its existing id means a disconnect reliably pauses both.
    return platform === 'HealthConnect' ? 'health-connect-installation-id' : INSTALLATION_ID_KEY;
  }
}
